// @ts-check
import { BETSLIP_MSG_STATUS, BETSLIP_MESSAGE_TYPE, BETSLIP_MESSAGE_KEY } from 'bto-models/betslip/const';
import { state } from '../state';
import { removeItem, changeItemOdd, changeTransitionToLiveItem } from './items';
import { addBetslipMessage } from './message.store';
import { getItemHashKey } from './helpers';
import { configStore } from './config.store';

/**
 * @typedef {import('bto-models/toast-notification/interfaces').BetslipNotification} BetslipNotification
 * @typedef {import('bto-models/betslip/interfaces').BetslipErrorMessage} BetslipErrorMessage
 */

/**
 * Handle incompatible items in betslip
 * @param {BetslipNotification} resp - betslip notification
 */
function incompatibleItems(resp) {
  if (resp.IncompatibleMatches.length > 0) {

    let message = {
      type: BETSLIP_MESSAGE_TYPE.WARNING,
      message: resp.ResponseMessage,
      subMessages: [],
    };
    message.subMessages = resp.IncompatibleMatches;

    addBetslipMessage(BETSLIP_MESSAGE_KEY.PLACEMENT_MESSAGE, message);
  }
}

/**
 * Handle max payout exceeded in betslip
 * @param {BetslipNotification} _resp - betslip notification
 */
function maxPayoutExceeded(_resp) {
  // FEAT: Implement this
}

function reMapBetItems(includeOutcome = false, changedOddKey = '') {
  const betslipItems = Object.values(state.items);
  if (betslipItems.length === 1 && Object.keys(state.raceOdds).length === 0 && changedOddKey) {
    return { [changedOddKey]: [betslipItems[0]] };
  }
  return betslipItems.reduce((acc, i) => {
    let complexKey = `${i.matchId}`;
    if (includeOutcome) {
      complexKey += `_${i.outcomeId}`;
    }
    if (!acc[complexKey]) {
      acc[complexKey] = [];
    }
    acc[complexKey].push(i);
    return acc;
  }, {});
}

function getChangedOddsParts(key = '') {
  const keys = key.split('_');
  const newOdd = keys.pop();
  const itemKey = keys.join('_');
  return { newOdd, itemKey };
}

function getChangeditemsParts(key = '') {
  const keys = key.split('_');
  const matchId = keys[0];
  const oldOutcomeId = configStore.settings.ALGO_ENABLED && state.algoItems[matchId] ? configStore.settings.ALGO_OUTCOME_ID : keys[1] ;
  const newOutcomeId = keys[2];
  const marketId = keys[3];
  const identifier = keys[4];
  const newOdd = keys[5];
  const itemKey =`${matchId}_${oldOutcomeId}`;
  return { newOdd, itemKey, marketId,matchId,newOutcomeId,  identifier};
}


/**
 * Handle items odd changed in betslip
 * @param {BetslipNotification} resp - betslip notification
 */
function changedOdds(resp) {
  const firstChangedOdd = getChangedOddsParts(resp.ChangeMatchesOdds[0].Key);
  const betItems = reMapBetItems(true, firstChangedOdd.itemKey);
  resp.ChangeMatchesOdds.forEach(co => {
    const { itemKey, newOdd } = getChangedOddsParts(co.Key);
    const betItem = betItems[itemKey];
    // If item is in betslip remove it
    if (betItem) {
      betItem.forEach(i => {
        changeItemOdd(i, newOdd);
      });
    }
  });
}

/**
 * Handle items transition to live in betslip
 * @param {BetslipNotification} resp - betslip notification
 */
function changedItems(resp) {
  const firstChangedOdd = getChangeditemsParts(resp.TransitionedToLiveItems[0].Key);
  const betItems = reMapBetItems(true, firstChangedOdd.itemKey);
  const message = resp.ResponseMessage
  resp.TransitionedToLiveItems.forEach(co => {
    const { itemKey, newOdd, marketId,newOutcomeId,identifier } = getChangeditemsParts(co.Key);
    const betItem = betItems[itemKey];
    // If item is in betslip remove it
    if (betItem) {
      betItem.forEach(i => {
        changeTransitionToLiveItem(i, newOdd,marketId,newOutcomeId,identifier, message);
      });
    }
  });
}
/**
 * Handle remove itemes from betslip
 * @param {BetslipNotification} resp - betslip notification
 */
function removeMatches(resp) {
  const betItems = reMapBetItems(false);
  let subMessages = [];
  resp.RemoveMatches.forEach(rm => {
    subMessages.push(rm.Value);
    const matchItems = betItems[rm.Key];
    if (matchItems) {
      matchItems.forEach(i => {
        removeItem(i);
      });
    }
  });
  return subMessages;
}

/**
 * Handle remove items betslip
 * @param {BetslipNotification} resp - betslip notification
 */
function removeItems(resp) {
  const betItems = reMapBetItems(true);
  let subMessages = [];
  resp.RemoveMatchOdds.forEach(rmo => {
    subMessages.push(rmo.Value);
    const betItem = betItems[rmo.Key];
    // If item is in betslip remove it
    if (betItem) {
      betItem.forEach(i => {
        removeItem(i);
      });
    }
  });
  return subMessages;
}

/**
 * Handle items changed in betslip
 * @param {BetslipNotification} resp - betslip notification
 */
function itemsChanged(resp) {
  let message = {
    type: BETSLIP_MESSAGE_TYPE.WARNING,
    message: resp.ResponseMessage,
    subMessages: [],
  };
  if (resp.RemoveMatchOdds.length > 0) {
    const subMsg = removeItems(resp);
    message.subMessages = message.subMessages.concat(subMsg);
  }
  if (resp.RemoveMatches.length > 0) {
    const subMsg = removeMatches(resp);
    message.subMessages = message.subMessages.concat(subMsg);
  }
  if (resp.ChangeMatchesOdds.length > 0) {
    changedOdds(resp);
  }
  if (resp.TransitionedToLiveItems.length > 0) {
    changedItems(resp);
  }
  addBetslipMessage(BETSLIP_MESSAGE_KEY.PLACEMENT_MESSAGE, message);
}

/**
 * Check if betslip needs to be updated
 * @param {BetslipNotification} resp 
 */
export function checkPlacement(resp) {
  switch (resp.ResponseCode) {
    case BETSLIP_MSG_STATUS.ITEMS_CHANGED:
      itemsChanged(resp);
      break;
    case BETSLIP_MSG_STATUS.MAX_PAYOUT_EXCEEDED:
      maxPayoutExceeded(resp);
      break;
    case BETSLIP_MSG_STATUS.INCOMPATIBLE_ITEMS:
      incompatibleItems(resp);
      break;
      case BETSLIP_MSG_STATUS.TRANSITION_TO_LIVE_ITEMS:
        itemsChanged(resp);
        break;
  }
}

/**
 * Function to accept odd changes on extracted single race each way odd
 */
export function accepOddChangeSingleEachWayItem() {
  const raceOdds = Object.keys(state.raceOdds);
  if (raceOdds.length === 1) {
    // Reset the race odd tick from ticket
    state.raceOdds = {};
    const betslipItem = state.items[raceOdds[0]];
    const singleStake = state.singles[raceOdds[0]];
    // Mutate betslip item to place the race each way as single outright item
    betslipItem.odd = betslipItem.raceOdd.odd;
    betslipItem.marketId = betslipItem.raceOdd.marketId;
    betslipItem.outcomeId = betslipItem.raceOdd.id;
    betslipItem.oddTypeOutcomeId = betslipItem.raceOdd.id;
    const itemKey = getItemHashKey(betslipItem);
    state.items = { [itemKey]: betslipItem };
    state.singles = { [itemKey]: singleStake };
  }
}